// ==========================================
// FILE: \src\screens\PaywallScreen.js
// ==========================================

import React, { useState, useEffect } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, ScrollView, 
  Platform, ActivityIndicator, Modal, useWindowDimensions, Linking 
} from 'react-native';
import { 
  ArrowLeft, Crown, CheckCircle2, 
  Gem, AlertCircle, Heart, Sparkles
} from 'lucide-react-native';

// --- IMPORT PER I PAGAMENTI E LO SCUDO ---
import Purchases from 'react-native-purchases';
import Constants from 'expo-constants';

// --- IMPORT FIREBASE PER L'IDENTIFICAZIONE UTENTE ---
import { auth } from '../config/firebase';

// IMPORTIAMO L'HOOK DEL CONTESTO PER LE TRADUZIONI CENTRALIZZATE
import { useWineContext } from '../context/WineContext';

const isMockEnvironment = Platform.OS === 'web' || Constants.executionEnvironment === 'storeClient';

// ============================================================================
// CONFIGURAZIONE LINK STRIPE CHECKOUT UFFICIALI (WEB APP)
// ============================================================================
const STRIPE_LINKS = {
  MONTHLY: 'https://buy.stripe.com/14AbJ30ie5E24e50HTefC00',
  ANNUAL: 'https://buy.stripe.com/bJe6oJe94d6u11T3U5efC01',
  LIFETIME: 'https://buy.stripe.com/8x2fZj8OK6I6eSJ4Y9efC02'
};

export default function PaywallScreen({ navigation }) {
  const { t } = useWineContext();
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customAlert, setCustomAlert] = useState({ visible: false, title: '', message: '', onConfirm: null });

  const { width } = useWindowDimensions();
  const isDesktop = width >= 900; 

  // UI di base: associamo stile e vantaggi in base al tipo di pacchetto pescando dal vocabolario
  const getUIConfig = (packageType) => {
    switch(packageType) {
      case 'MONTHLY':
      case '$rc_monthly':
        return {
          title: t('paywall.plans.monthly.title'), 
          period: t('paywall.plans.monthly.period'), 
          catchphrase: t('paywall.plans.monthly.catchphrase'),
          badge: null, 
          icon: <Heart color="#f43f5e" size={isDesktop ? 28 : 20} />, 
          color: '#f43f5e', 
          features: [t('paywall.plans.monthly.f1'), t('paywall.plans.monthly.f2'), t('paywall.plans.monthly.f3')]
        };
      case 'ANNUAL':
      case '$rc_annual':
        return {
          title: t('paywall.plans.annual.title'), 
          period: t('paywall.plans.annual.period'), 
          catchphrase: t('paywall.plans.annual.catchphrase'),
          badge: t('paywall.plans.annual.badge'), 
          icon: <Crown color="#d97706" size={isDesktop ? 28 : 20} />, 
          color: '#d97706', 
          features: [t('paywall.plans.annual.f1'), t('paywall.plans.annual.f2'), t('paywall.plans.annual.f3')]
        };
      case 'LIFETIME':
      case '$rc_lifetime':
        return {
          title: t('paywall.plans.lifetime.title'), 
          period: t('paywall.plans.lifetime.period'), 
          catchphrase: t('paywall.plans.lifetime.catchphrase'),
          badge: t('paywall.plans.lifetime.badge'), 
          icon: <Gem color="#a855f7" size={isDesktop ? 28 : 20} />, 
          color: '#a855f7', 
          isUltimate: true, 
          features: [t('paywall.plans.lifetime.f1'), t('paywall.plans.lifetime.f2'), t('paywall.plans.lifetime.f3')]
        };
      default: return null;
    }
  };

  // 1. CARICAMENTO DEI PACCHETTI DAL SERVER O MOCK WEB
  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        if (isMockEnvironment) {
          const mockPackages = [
            { identifier: 'mock_monthly', packageType: 'MONTHLY', product: { priceString: '3,99 €' } },
            { identifier: 'mock_annual', packageType: 'ANNUAL', product: { priceString: '39,90 €' } },
            { identifier: 'mock_lifetime', packageType: 'LIFETIME', product: { priceString: '99,90 €' } }
          ];
          setPackages(mockPackages);
          setSelectedPackageId('mock_annual');
          setIsFetching(false);
          return;
        }

        // --- AMBIENTE NATIVO REALE: Chiamiamo RevenueCat ---
        const offerings = await Purchases.getOfferings();
        if (offerings.current !== null && offerings.current.availablePackages.length !== 0) {
          const available = offerings.current.availablePackages;
          setPackages(available);
          
          const annualPkg = available.find(p => p.packageType === 'ANNUAL' || p.packageType === '$rc_annual');
          if (annualPkg) setSelectedPackageId(annualPkg.identifier);
          else setSelectedPackageId(available[0].identifier);
        }
      } catch (e) {
        console.error("Errore recupero pacchetti:", e);
      } finally {
        setIsFetching(false);
      }
    };

    fetchOfferings();
  }, []);

  // GESTIONE ACQUISTO ADATTIVA (WEB VIA STRIPE / MOBILE VIA REVENUECAT)
  const handlePurchase = async () => {
    const selectedPkg = packages.find(p => p.identifier === selectedPackageId);
    if (!selectedPkg) return;

    setIsProcessing(true);

    if (Platform.OS === 'web') {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setIsProcessing(false);
        setCustomAlert({
          visible: true,
          title: t('global.error'),
          message: "Devi essere autenticato per procedere all'acquisto."
        });
        return;
      }

      let baseUrl = STRIPE_LINKS.ANNUAL;
      if (selectedPkg.packageType === 'MONTHLY') baseUrl = STRIPE_LINKS.MONTHLY;
      if (selectedPkg.packageType === 'LIFETIME') baseUrl = STRIPE_LINKS.LIFETIME;

      const checkoutUrl = `${baseUrl}?client_reference_id=${currentUser.uid}&prefilled_email=${encodeURIComponent(currentUser.email || '')}`;
      
      setIsProcessing(false);
      Linking.openURL(checkoutUrl).catch(() => {
        setCustomAlert({
          visible: true,
          title: t('global.error'),
          message: "Impossibile aprire la pagina di pagamento Stripe."
        });
      });

    } else {
      try {
        const { customerInfo } = await Purchases.purchasePackage(selectedPkg);
        if (typeof customerInfo.entitlements.active['premium'] !== "undefined") {
          setCustomAlert({
            visible: true,
            title: t('global.success'),
            message: "Abbonamento attivato con successo! Benvenuto in VinoLogic Premium.",
            onConfirm: () => {
              setCustomAlert({ visible: false });
              navigation.goBack();
            }
          });
        }
      } catch (e) {
        if (!e.userCancelled) {
          setCustomAlert({
            visible: true,
            title: t('global.error'),
            message: e.message || "Errore durante il processo di acquisto."
          });
        }
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleRestore = async () => {
    if (Platform.OS === 'web') {
      setCustomAlert({
        visible: true,
        title: "Ripristino Acquisti",
        message: "Su Web il tuo stato si aggiorna automaticamente all'accesso. Se hai completato il pagamento su Stripe, ricarica la pagina."
      });
      return;
    }

    setIsProcessing(true);
    try {
      const customerInfo = await Purchases.restorePurchases();
      if (typeof customerInfo.entitlements.active['premium'] !== "undefined") {
        setCustomAlert({
          visible: true,
          title: t('global.success'),
          message: "Acquisti ripristinati con successo!"
        });
      } else {
        setCustomAlert({
          visible: true,
          title: "Nessun Abbonamento",
          message: "Non sono stati trovati acquisti attivi associati a questo account dello store."
        });
      }
    } catch (e) {
      setCustomAlert({
        visible: true,
        title: t('global.error'),
        message: "Impossibile ripristinare gli acquisti al momento."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderPlanCard = (pkg) => {
    const ui = getUIConfig(pkg.packageType);
    if (!ui) return null;

    const isSelected = selectedPackageId === pkg.identifier;

    return (
      <View key={pkg.identifier} style={styles.cardWrapper}>
        {ui.badge && (
          <View style={[styles.badge, { backgroundColor: ui.color }]}>
            <Text style={[styles.badgeText, { fontSize: isDesktop ? 12 : 9 }]} adjustsFontSizeToFit numberOfLines={1}>
              {ui.badge}
            </Text>
          </View>
        )}

        <TouchableOpacity 
          style={[
            styles.planCard,
            isSelected ? { borderColor: ui.color, backgroundColor: ui.isUltimate ? '#2e1065' : '#1e293b' } : { borderColor: '#334155' },
            isSelected && ui.isUltimate && styles.planCardUltimateSelected,
            isDesktop && ui.isUltimate && { transform: [{ scale: 1.05 }] }
          ]}
          activeOpacity={0.8}
          onPress={() => setSelectedPackageId(pkg.identifier)}
        >
          <View style={{ flex: 1 }}>
            <View style={styles.planCardTop}>
              {ui.icon}
              <Text style={[styles.planTitle, isSelected && {color: ui.color}, { fontSize: isDesktop ? 20 : 15 }]} adjustsFontSizeToFit numberOfLines={1}>
                {ui.title}
              </Text>
            </View>
            
            <View style={[styles.catchphraseBox, { backgroundColor: `${ui.color}15` }]}>
              <Text style={[styles.catchphraseText, { color: ui.color, fontSize: isDesktop ? 14 : 11 }]} adjustsFontSizeToFit numberOfLines={2}>
                "{ui.catchphrase}"
              </Text>
            </View>

            <View style={styles.priceRow}>
              <Text style={[styles.planPrice, { fontSize: isDesktop ? 34 : 22 }]} adjustsFontSizeToFit numberOfLines={1}>
                {pkg.product.priceString}
              </Text>
              <Text style={[styles.planPeriod, { fontSize: isDesktop ? 14 : 11 }]} adjustsFontSizeToFit numberOfLines={1}>{ui.period}</Text>
            </View>

            <View style={styles.featuresList}>
              {ui.features.map((feat, idx) => (
                <View key={idx} style={styles.featureItem}>
                  <CheckCircle2 color={ui.color} size={isDesktop ? 18 : 14} style={{marginRight: 6, flexShrink: 0}} />
                  <Text style={[styles.featureItemText, { fontSize: isDesktop ? 15 : 11 }]} adjustsFontSizeToFit numberOfLines={2}>{feat}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={[styles.radioCircle, isSelected && { borderColor: ui.color }, isDesktop && { width: 24, height: 24, borderRadius: 12 }]}>
            {isSelected && <View style={[styles.radioDot, { backgroundColor: ui.color }, isDesktop && { width: 12, height: 12, borderRadius: 6 }]} />}
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.mainContainer}>
      <View style={styles.darkBackground}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <ArrowLeft color="#fff" size={24} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>VinoLogic Premium</Text>
          <View style={{width: 40}} /> 
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          <View style={styles.heroSection}>
            <View style={styles.heroPill}>
              <Sparkles color="#facc15" size={isDesktop ? 24 : 18} />
              <Text style={[styles.heroTitle, { fontSize: isDesktop ? 20 : 16 }]}>{t('paywall.title')}</Text>
            </View>
            <Text style={styles.heroSub}>{t('paywall.sub')}</Text>
          </View>

          {isFetching ? (
            <View style={{ marginTop: 50, alignItems: 'center' }}>
              <ActivityIndicator size="large" color="#e11d48" />
              <Text style={{ color: '#64748b', marginTop: 16 }}>{t('paywall.loadingPreview')}</Text>
            </View>
          ) : (
            <View style={[styles.cardsRow, isDesktop && { maxWidth: 1000, gap: 16 }]}>
              {packages.map(renderPlanCard)}
            </View>
          )}

          <View style={{height: 160}} /> 
        </ScrollView>
      </View>

      {/* BOTTOM ACTION BAR ATTIVA */}
      <View style={styles.bottomBar}>
        <Text style={styles.legalDisclaimer}>
          {Platform.OS === 'web' 
            ? "I pagamenti web sono gestiti in modo sicuro tramite crittografia Stripe."
            : t('paywall.disclaimerText')
          }
        </Text>
        
        <View style={styles.purchaseBtnContainer}>
          <TouchableOpacity 
            style={[styles.purchaseBtn, { backgroundColor: '#e11d48' }, isProcessing && { opacity: 0.7 }]} 
            onPress={handlePurchase} 
            disabled={isProcessing || isFetching} 
            activeOpacity={0.8}
          >
            {isProcessing ? (
              <ActivityIndicator color="#ffffff" size="small" />
            ) : (
              <View style={styles.btnContentWrap}>
                <Crown color="#ffffff" size={24} />
                <View style={styles.btnTextWrap}>
                  <Text style={styles.purchaseBtnText}>
                    {Platform.OS === 'web' ? "Procedi al Checkout Stripe 💳" : "Attiva Ora la tua Cantina 🍷"}
                  </Text>
                  <Text style={styles.purchaseBtnSub}>
                    {Platform.OS === 'web' ? "Sblocca l'account su Web e Smartphone" : "Cancelli o modifichi quando vuoi"}
                  </Text>
                </View>
              </View>
            )}
          </TouchableOpacity>

          <TouchableOpacity style={{ marginTop: 12, alignItems: 'center' }} onPress={handleRestore}>
            <Text style={{ color: '#94a3b8', fontSize: 13, textDecorationLine: 'underline' }}>
              Ripristina Acquisti Esistenti
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <Modal transparent={true} visible={customAlert.visible} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBoxBase}>
            <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 16}}>
              <AlertCircle color="#3b82f6" size={28} />
              <Text style={styles.modalAlertTitle}>{customAlert.title}</Text>
            </View>
            <Text style={styles.modalAlertMessage}>{customAlert.message}</Text>
            
            <TouchableOpacity 
              style={styles.modalConfirmBtn} 
              onPress={() => {
                if (customAlert.onConfirm) {
                  customAlert.onConfirm();
                } else {
                  setCustomAlert({visible: false});
                }
              }}
            >
              <Text style={styles.modalConfirmText}>OK</Text>
            </TouchableOpacity>
            
          </View>
        </View>
      </Modal>

    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: { flex: 1, backgroundColor: '#0f172a' }, 
  darkBackground: { flex: 1, backgroundColor: '#0f172a' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingTop: Platform.OS === 'ios' ? 60 : 40, paddingBottom: 10 },
  backButton: { padding: 8, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 12 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#ffffff' },
  scrollContent: { paddingBottom: 20 },
  heroSection: { alignItems: 'center', marginTop: 16, marginBottom: 16, paddingHorizontal: 20 },
  heroPill: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.08)', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 30, borderWidth: 1, borderColor: 'rgba(255, 255, 255, 0.15)', marginBottom: 12 },
  heroTitle: { fontWeight: 'bold', color: '#ffffff', marginLeft: 8 },
  heroSub: { fontSize: 14, color: '#94a3b8', textAlign: 'center', lineHeight: 20, paddingHorizontal: 10 },
  cardsRow: { flexDirection: 'row', justifyContent: 'center', alignItems: 'stretch', paddingHorizontal: 10, gap: 8, alignSelf: 'center', width: '100%' },
  cardWrapper: { flex: 1, paddingTop: 16, position: 'relative' },
  planCard: { flex: 1, backgroundColor: '#0f172a', borderRadius: 16, padding: 12, borderWidth: 2, justifyContent: 'space-between' },
  planCardUltimateSelected: { boxShadow: '0px 0px 10px rgba(168, 85, 247, 0.5)', elevation: 6 },
  badge: { position: 'absolute', top: 0, alignSelf: 'center', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, zIndex: 10, elevation: 4, width: '90%', alignItems: 'center' },
  badgeText: { color: '#ffffff', fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' },
  planCardTop: { alignItems: 'center', marginBottom: 12, marginTop: 4 },
  planTitle: { fontWeight: 'bold', color: '#e2e8f0', marginTop: 4, textAlign: 'center' },
  catchphraseBox: { padding: 6, borderRadius: 8, marginBottom: 12, alignItems: 'center' },
  catchphraseText: { fontWeight: '600', fontStyle: 'italic', textAlign: 'center' },
  priceRow: { alignItems: 'center', marginBottom: 12 },
  planPrice: { fontWeight: 'extrabold', color: '#ffffff', textAlign: 'center' },
  planPeriod: { color: '#94a3b8', fontWeight: '500', textAlign: 'center' },
  featuresList: { borderTopWidth: 1, borderTopColor: '#334155', paddingTop: 12, gap: 10, marginBottom: 16 },
  featureItem: { flexDirection: 'row', alignItems: 'center' },
  featureItemText: { color: '#f8fafc', flex: 1, lineHeight: 18 },
  radioCircle: { height: 20, width: 20, borderRadius: 10, borderWidth: 2, borderColor: '#64748b', alignItems: 'center', justifyContent: 'center', alignSelf: 'center' },
  radioDot: { width: 10, height: 10, borderRadius: 5 },
  bottomBar: { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#0f172a', paddingHorizontal: 20, paddingTop: 12, paddingBottom: Platform.OS === 'ios' ? 34 : 10, borderTopWidth: 1, borderTopColor: '#1e293b' },
  purchaseBtnContainer: { maxWidth: 600, width: '100%', alignSelf: 'center' },
  purchaseBtn: { borderRadius: 16, padding: 16, elevation: 5, boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)' },
  btnContentWrap: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  btnTextWrap: { marginLeft: 12, alignItems: 'flex-start', flex: 1, paddingRight: 10 },
  purchaseBtnText: { color: '#ffffff', fontSize: 16, fontWeight: 'bold', marginBottom: 2 },
  purchaseBtnSub: { color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: '500' },
  legalDisclaimer: { fontSize: 11, color: '#64748b', textAlign: 'center', marginBottom: 10, lineHeight: 16 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.8)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBoxBase: { backgroundColor: '#1e293b', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400 },
  modalAlertTitle: { fontSize: 20, fontWeight: 'bold', color: '#f8fafc', marginLeft: 12 },
  modalAlertMessage: { fontSize: 16, color: '#cbd5e1', lineHeight: 24, marginBottom: 24 },
  modalConfirmBtn: { backgroundColor: '#3b82f6', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  modalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 }
});